/*
TRANE CONFIDENTIAL

2021 – 2022 Copyright Trane U.S. Inc.
All Rights Reserved.

NOTICE: All information contained herein is, and remains the property of
Trane U.S. Inc. The source code, object code, intellectual and technical
concepts contained herein are proprietary to Trane U.S. Inc. and may be
covered by patents and patent applications and are protected by trade secret
and copyright law. Dissemination of this information or reproduction of this
material is strictly forbidden unless prior written permission is obtained
from Trane U.S. Inc. Do not use this software without the prior written
permission of Trane U.S. Inc.
*/

/* @flow */
import * as React from "react";
import SchemaFieldsForm from "nexia_mobile/components/SchemaFieldsForm";
import validateFields, {
  getValueForField,
  isOrphanedField,
} from "nexia_mobile/validateFields";
import Schema from "nexia_mobile/models/schema";
import type { FieldValidationResult } from "nexia_mobile/validateFields";

export type SubmitHandlerArgument = {
  setServerErrors: ({ [fieldName: string]: Array<string> }) => void,
  validateBeforeSubmit: () => FieldValidationResult,
  getFieldValues: () => { [fieldName: string]: any },
};

type Props = {
  schema: Schema,
  onSubmitRequested?: (SubmitHandlerArgument) => void,
  children: (
    SubmitHandlerArgument & {
      schemaFieldsForm: React.Element<any>,
    }
  ) => React.Node,
  // This eslint flag is a false positive
  // eslint-disable-next-line react/no-unused-prop-types
  initialChanges?: { [fieldName: string]: any },
};

type State = {
  fieldValues: { [fieldName: string]: any },
  validateUnsetFields: boolean,
  serverErrors: { [fieldName: string]: Array<string> },
};

export default class SchemaFieldsFormManager extends React.Component<
  Props,
  State
> {
  _isMounted: boolean;
  _schemaFieldsForm: ?SchemaFieldsForm;

  state = this._initialState(this.props);

  _initialState(props: Props) {
    return {
      fieldValues: props.initialChanges || {},
      validateUnsetFields: false,
      serverErrors: {},
    };
  }

  UNSAFE_componentWillMount() {
    this._isMounted = true;
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (nextProps.schema !== this.props.schema) {
      this.setState(this._initialState(nextProps));
    }
  }

  _setServerErrors = (serverErrors: { [fieldName: string]: Array<string> }) => {
    if (!this._isMounted) {
      return;
    }

    this.setState({ serverErrors }, () => {
      const names = Object.keys(serverErrors);
      if (this._isMounted && names.length > 0) {
        if (this._schemaFieldsForm) {
          this._schemaFieldsForm.scrollToField(names[0]);
        }
      }
    });
  };

  _validateBeforeSubmit = () => {
    const { schema } = this.props;
    const { fieldValues } = this.state;
    const fields = schema.getSchemaFields();

    const validationResult = validateFields(fields, fieldValues, {
      validateUnsetFields: true,
    });

    if (validationResult.hasErrors && this._isMounted) {
      // Update state so that these errors are shown in the rendered output
      this.setState({ validateUnsetFields: true }, () => {
        if (this._schemaFieldsForm) {
          this._schemaFieldsForm.scrollToField(
            validationResult.nameOfFirstFieldWithError
          );
        }
      });
    }

    return validationResult;
  };

  _getFieldValues = () => {
    const { schema } = this.props;
    const { fieldValues } = this.state;
    const values = {};
    schema.getSchemaFields().forEach((field) => {
      values[field.name] = getValueForField(field, fieldValues);
    });
    return values;
  };

  _handleSubmit = () => {
    const { onSubmitRequested } = this.props;
    if (onSubmitRequested == null) {
      return;
    }

    onSubmitRequested({
      setServerErrors: this._setServerErrors,
      validateBeforeSubmit: this._validateBeforeSubmit,
      getFieldValues: this._getFieldValues,
    });
  };

  render() {
    const { schema, children, title, controllableThermostatsLabel } = this.props;
    const { fieldValues, validateUnsetFields, serverErrors } = this.state;

    const fields = schema.getSchemaFields();
    const { errors } = validateFields(fields, fieldValues, {
      validateUnsetFields,
    });

    const fieldsWithoutOrphans = fields.filter(
      (field) => !isOrphanedField(field, fields, fieldValues)
    );

    const schemaFieldsForm = (
      <SchemaFieldsForm
        ref={(inst) => (this._schemaFieldsForm = inst)}
        fields={fieldsWithoutOrphans}
        fieldErrors={{
          ...errors,
          ...serverErrors,
        }}
        fieldValues={fieldValues}
        title={title || ""}
        schema={schema}
        controllableThermostatsLabel={controllableThermostatsLabel}
        onChange={(name, newValue) => {
          this.setState((prevState) => {
            const nextState = {
              fieldValues: {
                ...prevState.fieldValues,
                [name]: newValue,
              },
              serverErrors: {
                ...prevState.serverErrors,
              },
            };

            fields.forEach((field) => {
              if (isOrphanedField(field, fields, nextState.fieldValues)) {
                delete nextState.fieldValues[field.name];
              }
            });

            if (prevState.serverErrors[name]) {
              delete nextState.serverErrors[name];
            }

            return nextState;
          });
        }}
        onSubmit={this._handleSubmit}
        onEnterPressed={this._handleSubmit}
      />
    );

    return children({
      schemaFieldsForm,
      setServerErrors: this._setServerErrors,
      validateBeforeSubmit: this._validateBeforeSubmit,
      getFieldValues: this._getFieldValues,
    });
  }
}



// WEBPACK FOOTER //
// ./src/components/SchemaFieldsFormManager.jsx